import type { BeforeInstallPromptEvent } from './install';

/**
 * Holds the browser's install prompt until the learner asks for it.
 *
 * Chrome fires `beforeinstallprompt` once, early, and only lets `prompt()` be
 * called from a gesture later. The event has to be caught in the entry chunk:
 * the card that uses it lives on a lazy route, and by the time that route has
 * loaded the event has usually already fired and gone. So `captureInstallPrompt`
 * runs from `App`, and the card subscribes to what it caught.
 *
 * Module state rather than React state so a tab change, which unmounts the
 * card, does not throw the prompt away.
 */

let held: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notifyAll(): void {
  for (const listener of listeners) listener();
}

/** Starts listening. Returns the cleanup, for tests and hot reloads. */
export function captureInstallPrompt(target: Window = window): () => void {
  const onPrompt = (event: Event): void => {
    // Stops Chrome's own mini-infobar so the card is the only offer.
    event.preventDefault();
    held = event as BeforeInstallPromptEvent;
    notifyAll();
  };
  const onInstalled = (): void => {
    held = null;
    notifyAll();
  };
  target.addEventListener('beforeinstallprompt', onPrompt);
  target.addEventListener('appinstalled', onInstalled);
  return () => {
    target.removeEventListener('beforeinstallprompt', onPrompt);
    target.removeEventListener('appinstalled', onInstalled);
  };
}

export function hasInstallPrompt(): boolean {
  return held !== null;
}

/** Subscribes to the prompt appearing or being used up. */
export function onInstallPromptChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

/**
 * Shows the browser's install dialog. Must be called from a tap.
 *
 * The event is single-use whatever the learner chooses, so it is dropped
 * either way; a dismissed prompt comes back only if the browser fires the
 * event again, which it does on a later visit.
 */
export async function promptInstall(): Promise<InstallOutcome> {
  const prompt = held;
  if (prompt === null) return 'unavailable';
  held = null;
  notifyAll();
  try {
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    return outcome;
  } catch {
    return 'dismissed';
  }
}

/** Test hook: forget any held prompt. */
export function resetInstallPrompt(): void {
  held = null;
  notifyAll();
}
